import { computed, inject, Injectable, signal } from '@angular/core';

import {
  PRODUCT_UPDATES_CONFIG,
  type ProductUpdatesConfig,
} from '../config/product-updates-config.provider';

export interface TranslationStrings {
  'header.title': string;
  'header.subtitle': string;
  'nav.whats_new': string;
  'nav.coming_soon': string;
  'nav.changelog': string;
  'nav.timeline': string;
  'filters.all': string;
  'filters.hire': string;
  'filters.manage': string;
  'filters.grow': string;
  'filters.platform': string;
  'categories.feature': string;
  'categories.improvement': string;
  'categories.changelog': string;
  'categories.new': string;
  'categories.coming_soon': string;
  'feed.read_more': string;
  'feed.no_updates': string;
  'feed.loading': string;
  'feed.error': string;
  'detail.back': string;
  'timeline.title': string;
  'timeline.feed': string;
  'timeline.timeline': string;
  'language.en': string;
  'language.de': string;
  'language.nl': string;
  'language.fr': string;
}

const TRANSLATIONS: Record<string, TranslationStrings> = {
  en: {
    'header.title': "What's New",
    'header.subtitle': 'Stay up to date with the latest product updates',
    'nav.whats_new': "What's new",
    'nav.coming_soon': 'Coming soon',
    'nav.changelog': 'Log',
    'nav.timeline': 'Timeline',
    'filters.all': 'All',
    'filters.hire': 'Hire',
    'filters.manage': 'Manage',
    'filters.grow': 'Grow',
    'filters.platform': 'Platform',
    'categories.feature': 'Feature',
    'categories.improvement': 'Improvement',
    'categories.changelog': 'Log',
    'categories.new': 'New',
    'categories.coming_soon': 'Coming soon',
    'feed.read_more': 'Read more',
    'feed.no_updates': 'No updates found',
    'feed.loading': 'Loading updates...',
    'feed.error': 'Failed to load updates. Please try again.',
    'detail.back': 'Back to updates',
    'timeline.title': 'Product Updates Timeline',
    'timeline.feed': 'Feed',
    'timeline.timeline': 'Timeline',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.fr': 'Français',
  },
  de: {
    'header.title': 'Neuigkeiten',
    'header.subtitle': 'Bleiben Sie über die neuesten Produktupdates informiert',
    'nav.whats_new': 'Neuigkeiten',
    'nav.coming_soon': 'Demnächst',
    'nav.changelog': 'Changelog',
    'nav.timeline': 'Zeitleiste',
    'filters.all': 'Alle',
    'filters.hire': 'Hire',
    'filters.manage': 'Manage',
    'filters.grow': 'Grow',
    'filters.platform': 'Platform',
    'categories.feature': 'Feature',
    'categories.improvement': 'Verbesserung',
    'categories.changelog': 'Changelog',
    'categories.new': 'Neu',
    'categories.coming_soon': 'Demnächst',
    'feed.read_more': 'Mehr lesen',
    'feed.no_updates': 'Keine Updates gefunden',
    'feed.loading': 'Updates werden geladen...',
    'feed.error': 'Fehler beim Laden der Updates. Bitte versuchen Sie es erneut.',
    'detail.back': 'Zurück zu Updates',
    'timeline.title': 'Produktupdates Zeitleiste',
    'timeline.feed': 'Feed',
    'timeline.timeline': 'Zeitleiste',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.fr': 'Français',
  },
  nl: {
    'header.title': 'Wat is er nieuw',
    'header.subtitle': 'Blijf op de hoogte van de laatste productupdates',
    'nav.whats_new': 'Wat is er nieuw',
    'nav.coming_soon': 'Binnenkort',
    'nav.changelog': 'Changelog',
    'nav.timeline': 'Tijdlijn',
    'filters.all': 'Alle',
    'filters.hire': 'Hire',
    'filters.manage': 'Manage',
    'filters.grow': 'Grow',
    'filters.platform': 'Platform',
    'categories.feature': 'Feature',
    'categories.improvement': 'Verbetering',
    'categories.changelog': 'Changelog',
    'categories.new': 'Nieuw',
    'categories.coming_soon': 'Binnenkort',
    'feed.read_more': 'Lees meer',
    'feed.no_updates': 'Geen updates gevonden',
    'feed.loading': 'Updates laden...',
    'feed.error': 'Updates laden mislukt. Probeer het opnieuw.',
    'detail.back': 'Terug naar updates',
    'timeline.title': 'Productupdates Tijdlijn',
    'timeline.feed': 'Feed',
    'timeline.timeline': 'Tijdlijn',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.fr': 'Français',
  },
  fr: {
    'header.title': 'Nouveautés',
    'header.subtitle': 'Restez informé des dernières mises à jour du produit',
    'nav.whats_new': 'Nouveautés',
    'nav.coming_soon': 'Bientôt',
    'nav.changelog': 'Changelog',
    'nav.timeline': 'Chronologie',
    'filters.all': 'Tous',
    'filters.hire': 'Hire',
    'filters.manage': 'Manage',
    'filters.grow': 'Grow',
    'filters.platform': 'Platform',
    'categories.feature': 'Fonctionnalité',
    'categories.improvement': 'Amélioration',
    'categories.changelog': 'Changelog',
    'categories.new': 'Nouveau',
    'categories.coming_soon': 'Bientôt',
    'feed.read_more': 'Lire la suite',
    'feed.no_updates': 'Aucune mise à jour trouvée',
    'feed.loading': 'Chargement des mises à jour...',
    'feed.error': 'Échec du chargement des mises à jour. Veuillez réessayer.',
    'detail.back': 'Retour aux mises à jour',
    'timeline.title': 'Chronologie des mises à jour',
    'timeline.feed': 'Fil',
    'timeline.timeline': 'Chronologie',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.fr': 'Français',
  },
};

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly config = inject<ProductUpdatesConfig>(PRODUCT_UPDATES_CONFIG);

  public readonly currentLocale = signal(this.detectInitialLocale());

  public readonly translations = computed(() => {
    const locale = this.currentLocale();
    return TRANSLATIONS[locale] || TRANSLATIONS[this.config.defaultLocale];
  });

  public translate(key: keyof TranslationStrings): string {
    return this.translations()[key] ?? key;
  }

  public setLocale(locale: string): void {
    if (this.config.availableLocales.includes(locale)) {
      this.currentLocale.set(locale);
    }
  }

  private detectInitialLocale(): string {
    const browserLang = navigator.language?.split('-')[0] || this.config.defaultLocale;
    return this.config.availableLocales.includes(browserLang)
      ? browserLang
      : this.config.defaultLocale;
  }
}
